import { useCallback, useContext } from 'react'

import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { faSparkles } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Divider, Tabs, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
// @ts-expect-error not typed yet
import { ChipSelector, academicFocusIcons } from '@scoir/college-search'
import { Link } from 'react-router-dom'

import { InterestItem } from 'store/metrics'
import { useCanViewMetrics, useSelectReachedAcademicFocus, useSelectReachedInterests } from 'store/metrics/hooks'

import ContentManagementContext, { TopicFilter } from '../../ContentManagementProvider/Context'

type SelectedOption = {
  label: string
  value: string
  icon?: string
}

const useStyles = makeStyles((theme) => ({
  chipTabsScroll: {
    minHeight: 'unset',
    '& .Mui-disabled': {
      opacity: '.2',
    },
  },
  chipBox: {
    '& .MuiGrid-container': {
      flexWrap: 'nowrap',
      overflow: 'auto',
    },
  },
  secondaryAlert: {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.dark,
    padding: theme.spacing(0.5, 1),
    '& .MuiAlert-message': {
      padding: 0,
      display: 'flex',
      alignItems: 'center',
    },
  },
  secondaryDark: {
    color: theme.palette.secondary.dark,
    '&:hover': {
      color: theme.palette.secondary.dark,
    },
  },
}))

const mapToOption = (items: InterestItem[]) =>
  items.map((item) => ({
    label: item.name,
    value: item.name,
    icon: academicFocusIcons[item.name]?.icon,
    selectedIcon: academicFocusIcons[item.name]?.selectedIcon,
  }))

const TopInterests = () => {
  const styles = useStyles()
  const ROOT = import.meta.env.BASE_URL
  const interests = useSelectReachedInterests()
  const academicFocus = useSelectReachedAcademicFocus()
  const showCollegeMetrics = useCanViewMetrics()

  const { filterByTopics, handleTableFiltering } = useContext(ContentManagementContext)

  const getSelectedAcademicFocus = useCallback(() => {
    const topics: SelectedOption[] = []
    filterByTopics?.AcademicFocus.forEach((focus) => {
      topics.push({
        label: focus,
        value: focus,
      })
    })

    return topics
  }, [filterByTopics?.AcademicFocus])

  const getSelectedCategories = useCallback(() => {
    const topics: SelectedOption[] = []
    filterByTopics?.Categories.forEach((interest) => {
      topics.push({
        label: interest,
        value: interest,
      })
    })

    return topics
  }, [filterByTopics?.Categories])

  const handleTopicsFilter = (value: SelectedOption[], type: 'AcademicFocus' | 'Categories') => {
    const lastSelected = value[value.length - 1]
    if (!value || !lastSelected) {
      handleTableFiltering('Topics')
      return
    }
    const selected: TopicFilter = { AcademicFocus: [], Categories: [] }
    if (type === 'AcademicFocus') {
      selected.AcademicFocus = [lastSelected?.value]
    }
    if (type === 'Categories') {
      selected.Categories = [lastSelected?.value]
    }
    handleTableFiltering('Topics', selected)
  }

  if (!showCollegeMetrics) {
    return null
  }

  return (
    <>
      <Divider />
      <Box
        m={2}
        display="flex"
        flexDirection="column"
        gridGap={16}
      >
        <Box
          display="flex"
          gridGap={40}
        >
          <Box
            display="flex"
            flexDirection="column"
            gridGap={16}
            flex={1}
            maxWidth="48%"
          >
            <Typography>Top Interests</Typography>
            <Tabs
              variant="scrollable"
              className={styles.chipTabsScroll}
            >
              <Box
                display="flex"
                gridGap={8}
                flexWrap="wrap"
                className={styles.chipBox}
              >
                <ChipSelector
                  key="interests"
                  options={mapToOption(interests)}
                  selected={getSelectedCategories()}
                  onUpdateSelection={(value) => handleTopicsFilter(value, 'Categories')}
                />
              </Box>
            </Tabs>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gridGap={16}
            flex={1}
            maxWidth="48%"
          >
            <Typography>Top Academic Focuses</Typography>
            <Tabs
              variant="scrollable"
              className={styles.chipTabsScroll}
            >
              <Box
                display="flex"
                gridGap={8}
                flexWrap="wrap"
                className={styles.chipBox}
              >
                <ChipSelector
                  key="academicFocus"
                  options={mapToOption(academicFocus)}
                  selected={getSelectedAcademicFocus()}
                  onUpdateSelection={(value) => handleTopicsFilter(value, 'AcademicFocus')}
                />
              </Box>
            </Tabs>
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="body2"
            color="textSecondary"
          >
            <FontAwesomeIcon icon={faInfoCircle} /> Based on your reached students.
          </Typography>
          <Alert
            className={styles.secondaryAlert}
            icon={
              <FontAwesomeIcon
                icon={faSparkles}
                className={styles.secondaryDark}
              />
            }
          >
            Want deeper insights?{' '}
            <Button
              component={Link}
              to={`${ROOT}/dashboard`}
              className={styles.secondaryDark}
            >
              View Dashboard{' '}
            </Button>
          </Alert>
        </Box>
      </Box>
    </>
  )
}

export default TopInterests
